import {
  IconHeart,
  IconHeartFilled,
  IconMoon,
  IconPhotoFilled,
  IconPlaneDeparture,
  IconToolsKitchen2,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useLocaleContext } from "src/context/locale-context";
import { favoritesActions, selectIsFavorited } from "src/store/favorites";
import { getCharterDealImage } from "src/components/features/home/CharterDeals/charterDealImage";
import { getCharterText } from "src/components/features/home/CharterDeals/charterDealsText";
import classes from "./ProductCards.module.css";

const BADGE_CLASS = {
  rose: classes.vacationBadgeRose,
  sky: classes.vacationBadgeSky,
  mint: classes.vacationBadgeMint,
  amber: classes.vacationBadgeAmber,
  violet: classes.vacationBadgeViolet,
};

// Same info blocks as desktop's CharterDealCard (badge, nights/meal/departure, price "no .../per
// person"), just resized for the mobile scroll card — was deliberately image+name+price-only
// before, but that left the card looking empty/disproportionate once the photo grew taller.
export function VacationDealCard({ deal }) {
  const { getStringRes, currencySymbol } = useLocaleContext();
  const dispatch = useDispatch();
  const [imgError, setImgError] = useState(false);
  const imageUrl = getCharterDealImage(deal);
  const hasImage = !imgError && !!imageUrl;
  const favoriteId = `charter-${deal.id}`;
  const isFavorite = useSelector((state) => selectIsFavorited(state, favoriteId));

  const onToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(favoritesActions.toggleFavorite({ id: favoriteId, type: "charter", data: deal }));
    if (!isFavorite) {
      toast.success(getStringRes("favorites.toast.saved"));
    }
  };

  const body = (
    <>
      <div className={classes.vacationImageWrap}>
        {hasImage ? (
          <img
            src={imageUrl}
            alt={deal.title}
            className={classes.vacationImage}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={classes.vacationImageFallback}>
            <IconPhotoFilled size="35%" color="var(--ui-4)" />
          </div>
        )}
        {deal.badge && (
          <span
            className={`${classes.vacationBadge} ${BADGE_CLASS[deal.badgeTone] || classes.vacationBadgeSky}`}>
            {deal.badge}
          </span>
        )}
      </div>
      <div className={classes.vacationBody}>
        <h3 className={classes.vacationName}>{deal.title}</h3>

        {Number.isFinite(deal.nights) && (
          <div className={classes.vacationMetaRow}>
            <IconMoon size={15} stroke={1.75} />
            <span>
              {deal.nights} {getCharterText(getStringRes, "charter.deals.nights")}
            </span>
          </div>
        )}
        {deal.meal && (
          <div className={classes.vacationMetaRow}>
            <IconToolsKitchen2 size={15} stroke={1.75} />
            <span>{deal.meal}</span>
          </div>
        )}
        {deal.departure && (
          <div className={classes.vacationMetaRow}>
            <IconPlaneDeparture size={15} stroke={1.75} />
            <span>{deal.departure}</span>
          </div>
        )}

        <hr className={classes.vacationDivider} />

        <div className={classes.vacationPriceRow}>
          {Number.isFinite(deal.price) ? (
            <>
              <span className={classes.priceLabel}>
                {getCharterText(getStringRes, "charter.deals.priceFrom")}
              </span>
              <div className={classes.vacationPriceLine}>
                <span className={classes.priceAmountSm}>
                  {deal.price.toFixed(0)} {currencySymbol}
                </span>
                <span className={classes.vacationPricePerPerson}>
                  {getCharterText(getStringRes, "charter.deals.perPerson")}
                </span>
              </div>
            </>
          ) : (
            <span className={classes.priceNone}>{getStringRes("cheap.hotels.price.none")}</span>
          )}
        </div>
      </div>
    </>
  );

  // Waavo links are external (open in a new tab); an internal fallback (e.g. /charter-flights)
  // should navigate within the app instead of a full page reload in a new tab.
  const isExternal = deal.link?.startsWith("http");

  const cardLink = !deal.link ? (
    <div className={classes.vacationCard}>{body}</div>
  ) : isExternal ? (
    <a href={deal.link} target="_blank" rel="noopener noreferrer" className={classes.vacationCard}>
      {body}
    </a>
  ) : (
    <Link to={deal.link} className={classes.vacationCard}>
      {body}
    </Link>
  );

  return (
    <div className={classes.vacationFavoriteWrap}>
      <button
        type="button"
        className={classes.vacationFavoriteButton}
        aria-label={getStringRes("cheap.hotels.favorite")}
        onClick={onToggleFavorite}>
        {isFavorite ? (
          <IconHeartFilled size={14} color="#ff2965" />
        ) : (
          <IconHeart size={14} stroke={1.75} />
        )}
      </button>
      {cardLink}
    </div>
  );
}
